/**
 * Utility functions for grouping HACCP log entries into collapsible sections.
 * - Trial section: entries dated on or before TRIAL_CUTOFF_DATE
 * - Monthly sections: entries after TRIAL_CUTOFF_DATE, grouped by month
 */

/** Cutoff date for trial period - entries on or before this date go into Trial section */
export const TRIAL_CUTOFF_DATE = '2026-01-27';

/** Represents a grouped section of HACCP items */
export interface HaccpSection<T> {
  /** Section key: 'trial' or 'YYYY-MM' format */
  key: string;
  /** Display label: 'Trial Period' or 'February 2026' format */
  label: string;
  /** Whether this is the trial section */
  isTrialSection: boolean;
  /** Items in this section */
  items: T[];
}

/**
 * Extract date string (YYYY-MM-DD) from various date formats.
 * Handles ISO datetime strings, date-only strings, and timestamps.
 */
export function extractDateString(dateValue: string | number | Date): string {
  if (!dateValue) return '';

  if (typeof dateValue === 'string') {
    // Handle ISO datetime (2026-01-23T10:30:00) or date-only (2026-01-23)
    return dateValue.slice(0, 10);
  }

  if (dateValue instanceof Date) {
    return dateValue.toISOString().slice(0, 10);
  }

  // Handle timestamp
  return new Date(dateValue).toISOString().slice(0, 10);
}

/**
 * Get month key from date string (YYYY-MM format)
 */
export function getMonthKey(dateString: string): string {
  const extracted = extractDateString(dateString);
  return extracted.slice(0, 7); // YYYY-MM
}

/**
 * Format month key to display label (e.g., '2026-02' -> 'February 2026')
 */
export function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Check if a date is in the trial period (on or before cutoff date)
 */
export function isTrialPeriod(dateString: string): boolean {
  const extracted = extractDateString(dateString);
  return extracted <= TRIAL_CUTOFF_DATE;
}

/**
 * Group items into Trial and monthly sections.
 *
 * @param items - Array of items to group
 * @param getDateFn - Function to extract date from each item
 * @returns Array of sections, with monthly sections sorted newest first, trial section last
 */
export function groupItemsBySection<T>(
  items: T[],
  getDateFn: (item: T) => string | undefined | null
): HaccpSection<T>[] {
  const trialItems: T[] = [];
  const monthlyMap = new Map<string, T[]>();

  // Group items
  for (const item of items) {
    const dateValue = getDateFn(item);
    if (!dateValue) continue;

    if (isTrialPeriod(dateValue)) {
      trialItems.push(item);
    } else {
      const monthKey = getMonthKey(dateValue);
      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, []);
      }
      monthlyMap.get(monthKey)!.push(item);
    }
  }

  // Build sections array
  const sections: HaccpSection<T>[] = [];

  // Add monthly sections sorted by month key descending (newest first)
  const sortedMonthKeys = Array.from(monthlyMap.keys()).sort((a, b) => b.localeCompare(a));

  for (const monthKey of sortedMonthKeys) {
    sections.push({
      key: monthKey,
      label: formatMonthLabel(monthKey),
      isTrialSection: false,
      items: monthlyMap.get(monthKey)!
    });
  }

  // Add trial section at the end (if it has items)
  if (trialItems.length > 0) {
    sections.push({
      key: 'trial',
      label: 'Trial Period',
      isTrialSection: true,
      items: trialItems
    });
  }

  return sections;
}

/**
 * Get default expanded state for sections.
 * Monthly sections are expanded by default, trial section is collapsed.
 */
export function getDefaultExpandedState(sections: HaccpSection<unknown>[]): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const section of sections) {
    state[section.key] = false; // All sections collapsed by default
  }
  return state;
}
